// 📄 components/abap/ProgramaCatalogGrid.tsx
// Grid filtrable de tarjetas del Catálogo de Programas — componente
// compartido entre la sección de Admin (CRUD completo, ver
// ProgramaCatalogSection.tsx) y la vista de Herramientas del proyecto
// (solo lectura, ver ProgramasCatalogo.tsx). Mismo patrón exacto que
// BadiCatalogGrid.tsx, con el agregado de mostrar las relaciones N:N
// (badiRefIds/transaccionRefIds) como chips resueltos contra los otros
// 2 catálogos.
import { useMemo, useState } from 'react';
import { Search, Copy, Check, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Programa, SOLUCIONES_PROGRAMA, solucionProgramaMeta } from '../../services/catalogoProgramasService.ts';
import { BadiObjeto } from '../../services/catalogoBadisService.ts';
import { TransaccionSap } from '../../services/catalogoTransaccionesService.ts';

// Item del grid: Programa (catálogo global) con los campos extra que solo
// trae un desarrollo Z (ver programaZService.ts) — todos opcionales a
// propósito, mismo patrón que BadiCatalogGrid.tsx/TransactionCatalogGrid.tsx.
type ProgramaCatalogItem = Programa & {
  responsable?: string;
  sistemasDisponibles?: string[];
};

interface ProgramaCatalogGridProps {
  programas: ProgramaCatalogItem[];
  // Para resolver los chips de relaciones a un nombre legible — si faltan,
  // el chip muestra el id crudo (mejor que ocultarlo silenciosamente).
  badis: BadiObjeto[];
  transacciones: TransaccionSap[];
  esZ?: boolean;
  readOnly?: boolean;
  onEdit?: (programa: Programa) => void;
  onDelete?: (programa: Programa) => void;
  // Cross-nav dentro del panel Admin (cambia de sección) — mismo criterio
  // que InterfaceCatalogSection.tsx: no es un deep-link al item puntual,
  // solo lleva al catálogo relacionado.
  onVerEnBadis?: () => void;
  onVerEnTransacciones?: () => void;
}

export default function ProgramaCatalogGrid({
  programas, badis, transacciones, esZ = false, readOnly = false, onEdit, onDelete, onVerEnBadis, onVerEnTransacciones
}: ProgramaCatalogGridProps) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);

  const badiNombrePorId = useMemo(() => new Map(badis.map((b) => [b.id, b.nombre])), [badis]);
  const transaccionNombrePorId = useMemo(() => new Map(transacciones.map((t) => [t.id, t.transaccion])), [transacciones]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return programas.filter((p) => {
      if (filtroSolucion && p.solucion !== filtroSolucion) return false;
      if (!q) return true;
      return [p.nombre, p.nombreTecnico, p.descripcion].join(' ').toLowerCase().includes(q);
    });
  }, [programas, busqueda, filtroSolucion]);

  const handleCopiar = async (programa: Programa) => {
    const valor = programa.nombreTecnico || programa.nombre;
    try {
      await navigator.clipboard.writeText(valor);
      setCopiadoId(programa.id);
      setTimeout(() => setCopiadoId((prev) => (prev === programa.id ? null : prev)), 1500);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, nombre técnico..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select className={inputClass} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)}>
          <option value="">Todas las soluciones</option>
          {SOLUCIONES_PROGRAMA.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {filtrados.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          {programas.length === 0 ? 'Todavía no hay programas registrados en el catálogo.' : 'Ningún programa coincide con el filtro.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtrados.map((programa) => {
            const solMeta = solucionProgramaMeta(programa.solucion);
            return (
              <div key={programa.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white leading-snug break-words">{programa.nombre}</h4>
                  <button
                    type="button"
                    onClick={() => handleCopiar(programa)}
                    title="Copiar Nombre técnico"
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiadoId === programa.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {esZ && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                    {solMeta.label}
                  </span>
                  {programa.nombreTecnico && (
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border text-gray-300 bg-white/5 border-white/10">
                      {programa.nombreTecnico}
                    </span>
                  )}
                </div>

                {programa.descripcion && (
                  <p className="text-xs text-gray-400 leading-relaxed">{programa.descripcion}</p>
                )}

                {(programa.badiRefIds.length > 0 || programa.transaccionRefIds.length > 0) && (
                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                    {programa.badiRefIds.length > 0 && (
                      <p className="text-[11px] text-gray-500 flex flex-wrap items-center gap-1">
                        <span className="text-gray-600">BAdIs:</span>
                        {programa.badiRefIds.map((id) => (
                          <span key={id} className="text-purple-300 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded-full">
                            {badiNombrePorId.get(id) || id}
                          </span>
                        ))}
                        {onVerEnBadis && (
                          <button type="button" onClick={onVerEnBadis} title="Ver Catálogo de BAdIs" className="text-blue-400 hover:text-blue-300">
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </p>
                    )}
                    {programa.transaccionRefIds.length > 0 && (
                      <p className="text-[11px] text-gray-500 flex flex-wrap items-center gap-1">
                        <span className="text-gray-600">Transacciones:</span>
                        {programa.transaccionRefIds.map((id) => (
                          <span key={id} className="font-mono text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full">
                            {transaccionNombrePorId.get(id) || id}
                          </span>
                        ))}
                        {onVerEnTransacciones && (
                          <button type="button" onClick={onVerEnTransacciones} title="Ver Catálogo de Transacciones" className="text-blue-400 hover:text-blue-300">
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {esZ && (programa.responsable || (programa.sistemasDisponibles && programa.sistemasDisponibles.length > 0)) && (
                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                    {programa.responsable && (
                      <p className="text-[11px] text-gray-500">Responsable: <span className="text-gray-300">{programa.responsable}</span></p>
                    )}
                    {programa.sistemasDisponibles && programa.sistemasDisponibles.length > 0 && (
                      <div className="flex items-center gap-1">
                        {programa.sistemasDisponibles.map((s) => (
                          <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!readOnly && (onEdit || onDelete) && (
                  <div className="flex items-center justify-end gap-1.5 pt-2 mt-auto border-t border-white/5">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(programa)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(programa)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
